import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { getContentPropertyValue, CONTENT_PLATFORM_PROPERTY_NAME, getAssociatedContentTranslations, getMlangGroupSize, useAuthInfoProvider } from 'content-management-data-lib';
import { InlineLabel } from 'foundations-components/transitional/components/InlineLabel';
import { Select } from 'foundations-components/components/Select';
import { Select as TransitionalSelect } from 'foundations-components/transitional/components/Select';
import { ALL_LANGUAGE_VARIATIONS } from '../constants/languageVariations';
import { useLanguageVariationsProvider } from '../providers/LanguageVariationsProvider';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getLanguageNameFromKey = key => {
  if (key) {
    return I18n.text(`SharedI18nStrings.languageNames.${key}`);
  }
  return I18n.text('content-management-details-lib.header.headerDetails.noLanguage');
};
const getLanguageOptions = ({
  contentId,
  language,
  mLangGroupSize,
  translatedContent,
  translatedFromId
}) => {
  const numberOfLanguages = I18n.formatNumber(mLangGroupSize);
  const allOptions = [{
    text: I18n.text('content-management-details-lib.header.headerDetails.translationsSelector.allLanguages', {
      numberOfLanguages
    }),
    value: ALL_LANGUAGE_VARIATIONS
  }];
  const translationOptions = translatedContent.map(translation => {
    const tId = getContentPropertyValue(translation, CONTENT_PLATFORM_PROPERTY_NAME.ID);
    const tMasterId = getContentPropertyValue(translation, CONTENT_PLATFORM_PROPERTY_NAME.TRANSLATED_FROM_ID);
    const tLang = getContentPropertyValue(translation, CONTENT_PLATFORM_PROPERTY_NAME.LANGUAGE);
    return {
      text: !tMasterId || tId === translatedFromId ? I18n.text('content-management-details-lib.header.headerDetails.translationsSelector.mainLanguage', {
        language: getLanguageNameFromKey(tLang)
      }) : getLanguageNameFromKey(tLang),
      value: tId
    };
  });
  translationOptions.push({
    text: !translatedFromId ? I18n.text('content-management-details-lib.header.headerDetails.translationsSelector.mainLanguage', {
      language: getLanguageNameFromKey(language)
    }) : getLanguageNameFromKey(language),
    value: contentId
  });
  translationOptions.sort((a, b) => a.text > b.text ? 1 : a.text < b.text ? -1 : 0);
  return allOptions.concat(translationOptions);
};
const LanguageTranslationSelector = ({
  contentObject,
  contentId,
  onChangeLanguageVariation
}) => {
  const {
    isCurrentlyViewingAllLangVariations,
    setIsCurrentlyViewingAllLangVariations
  } = useLanguageVariationsProvider();
  const {
    getIsUngatedForCMPTrellisMigration
  } = useAuthInfoProvider();
  const isUngatedForCMPTrellisMigration = getIsUngatedForCMPTrellisMigration();
  const translatedFromId = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.TRANSLATED_FROM_ID);
  const translatedContent = getAssociatedContentTranslations(contentObject);
  const language = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.LANGUAGE);
  const mLangGroupSize = getMlangGroupSize(contentObject);
  const options = useMemo(() => {
    return getLanguageOptions({
      contentId,
      language,
      mLangGroupSize,
      translatedContent,
      translatedFromId
    });
  }, [contentId, language, mLangGroupSize, translatedContent, translatedFromId]);
  const handleChangeLanguage = event => {
    const {
      value
    } = event.target;
    if (value === ALL_LANGUAGE_VARIATIONS) {
      setIsCurrentlyViewingAllLangVariations(true);
    } else {
      setIsCurrentlyViewingAllLangVariations(false);
      onChangeLanguageVariation(value);
    }
  };
  if (isUngatedForCMPTrellisMigration) {
    return /*#__PURE__*/_jsx(InlineLabel, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "content-management-details-lib.header.headerDetails.translationsSelector.label"
      }),
      maxInlineSize: "250px",
      children: /*#__PURE__*/_jsx(Select, {
        "data-test-id": "translation-selector",
        onSelectedOptionChange: ({
          value
        }) => {
          if (value === ALL_LANGUAGE_VARIATIONS) {
            setIsCurrentlyViewingAllLangVariations(true);
          } else {
            setIsCurrentlyViewingAllLangVariations(false);
            onChangeLanguageVariation(`${value}`);
          }
        },
        options: options,
        selectedValues: isCurrentlyViewingAllLangVariations ? [ALL_LANGUAGE_VARIATIONS] : [contentId],
        menuInlineSize: 250
      })
    });
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "content-management-details-lib.header.headerDetails.translationsSelector.label"
    }), /*#__PURE__*/_jsx(TransitionalSelect, {
      buttonUse: "transparent",
      "data-test-id": "translation-selector",
      menuWidth: "auto",
      onChange: handleChangeLanguage,
      options: options,
      value: isCurrentlyViewingAllLangVariations ? ALL_LANGUAGE_VARIATIONS : contentId
    })]
  });
};
export default LanguageTranslationSelector;